import React from 'react';
import { HSpacer, Text } from '@/components/DesignSystem';
import { Category } from '@/themes/variant-interfaces/Category';
import Star from '@mui/icons-material/Star';
import { Stack, SxProps, Theme, useTheme } from '@mui/material';
import { RewardsType } from '@shared/enums/RewardsType';
import { RewardsUom, RewardsUomPluralization } from '@shared/enums/RewardsUom';
import { formatPercent } from '@shared/utilities';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';

interface PointsTagProps {
  labelStyle?: SxProps<Theme>,
  points: number,
  rewardsType?: RewardsType,
  starHeight?: string,
  starWidth?: string,
  uom?: RewardsUom,
  textCategory?: Category,
  variant?: 'filled' | 'text'
}

export const PointsTag = ({
  labelStyle,
  points,
  rewardsType = RewardsType.Dollars,
  starHeight = '12px',
  starWidth = '12px',
  textCategory = 'label-small',
  uom,
  variant = 'text',
}: PointsTagProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const displayValue = rewardsType === RewardsType.Dollars ? points : points * 100;

  let text = rewardsType === RewardsType.Percent
    ? `${formatPercent(displayValue, i18next)} ${t($ => $.common.units.points)}`
    : t($ => $.common.units.pointsCount, { count: displayValue });
  if (uom) {
    if (uom === RewardsUom.Dollars) {
      text += ` ${t($ => $.shared.pointsTag.perDollarSpent)}`;
    } else {
      text += ` ${t($ => $.shared.pointsTag.perUnitPurchased, { 
        unit: RewardsUomPluralization[uom].singular,
      })}`;
    }
  }
  const content = (
    <Stack alignItems="center" direction="row" py="2px">
      <Star
        sx={{
          color: variant === 'filled' ? theme.palette.secondary.main : theme.palette.rewardsYellow,
          height: starHeight,
          width: starWidth,
        }}
      />
      <HSpacer size="2"/>
      <Text
        category={textCategory}
        noWrap
        sx={{
          ...labelStyle,
          lineHeight: 1,
        }}
      >
        {text}
      </Text>
    </Stack>
  );

  if (variant === 'text') {
    return content;
  }
  return (
    <Stack
      alignItems="center"
      bgcolor={theme.palette.rewardsYellow}
      borderRadius="2px"
      direction="row"
      padding="3px 7px 3px 5px"
      width="fit-content"
    >
      {content}
    </Stack>
  );
};
